import { ActivatedRoute } from '@angular/router';
import { Injectable } from '@angular/core';
import { Observable, combineLatest, of } from 'rxjs';
import { SponsorStudy } from '../../sponsor-dashboard/sponsor-study';
import { map, flatMap, switchAll, switchMap, takeWhile, distinctUntilChanged } from 'rxjs/operators';
import { LabTestResult, LabTestResultsReadModel, Flag } from '../../lab-test-results/lab-test-result';
import { HttpClient } from '@angular/common/http';
import { Settings } from '../../core/settings';
import { deserialize } from 'santee-dcts';
import { LabTestsIdentityReadModel, LabTestIdentity } from './lab-test';
import { uniqBy, isEmpty } from 'lodash';
import { LOAD_ALL_RESULTS_QUERY_PARAMETER, getOptionalParametersForSTRQuery } from '../../on-screen-results/standard-test-results/standart-test-result-query-parameters';
import { SponsorStudySiteIdentity } from '../sites/sponsor-study-site-identity';

@Injectable()
export class SponsorLabResultsStorage {

    constructor(
        private readonly settings: Settings,
        private readonly http: HttpClient,
        private readonly route: ActivatedRoute
    ) { }

    public loadSponsorSubjectLabResults(study$: Observable<SponsorStudy>, subjectId$: Observable<string>): Observable<LabTestResult[]> {
        return combineLatest([study$, subjectId$])
            .pipe(
                map(([study, subjectId]: [SponsorStudy, string]) => {
                    if (isEmpty(subjectId)) {
                        return of([]);
                    }

                    return this.http
                        .get(`${this.settings.apiUrl}/sponsor/study/${study.protocolId}/subjects/${subjectId}/lab-test-results`)
                        .pipe(map((response) => deserialize(response, LabTestResultsReadModel).results));
                }),
                flatMap((x) => of(of(null), x)),
                switchAll()
            );
    }

    public loadLabTestsIdentities(study: SponsorStudy): Observable<LabTestIdentity[]> {
        return this.http
            .get(`api/sponsor/study/${study.protocolId}/test-identities`)
            .pipe(map(result => deserialize(result, LabTestsIdentityReadModel).results));
    }

    public loadLabTestsData(study: SponsorStudy, zcTestCode: string): Observable<LabTestResult[]> {
        return this.http
            .get(`api/sponsor/study/${study.protocolId}/lab-test-results/${zcTestCode}`)
            .pipe(
                map((response) => this.processRawData(response))
            );
    }

    public loadLabTestsSiteData(selectedSite: Observable<SponsorStudySiteIdentity>): Observable<LabTestResult[]> {
        const loadAllQueryParam$ = this.route.queryParams
            .pipe(
                map(p => !!p[LOAD_ALL_RESULTS_QUERY_PARAMETER]),
                distinctUntilChanged(),
                takeWhile(v => !v, true));

        return selectedSite
            .pipe(
                switchMap(site => combineLatest(of(site), loadAllQueryParam$)),
                map(([site, loadAllQueryParam]: [SponsorStudySiteIdentity, boolean]) => {
                    if (!site) {
                        return of([] as LabTestResult[]);
                    }
                    const optionalParams = getOptionalParametersForSTRQuery(loadAllQueryParam, site, this.route.snapshot.queryParams);
                    return this.http
                        .get(`${this.settings.apiUrl}/sites/${site.siteId}/lab-test-results${optionalParams}`)
                        .pipe(
                            map((response) => {
                                const results = this.processRawData(response);
                                return results;
                            }));
                }
                ),
                flatMap((x) => of(of(null), x)),
                switchAll());
    }

    private processRawData(data: any) {
        const results = deserialize(data, LabTestResultsReadModel).results;
        this.setTrendsFlagsAndOrderNums(results);
        return results;
    }

    private setTrendsFlagsAndOrderNums(results: LabTestResult[]) {
        const subjects = uniqBy(results, r => r.ctSubjectId).map(r => r.ctSubjectId);
        const subjectsFlags: { [subjectKey: string]: Flag[] } = {};
        for (let res of results) {
            if (!subjectsFlags[res.zcSubjectId]) {
                subjectsFlags[res.zcSubjectId] = [];
            }

            for (let flag of res.flags) {
                if (!subjectsFlags[res.zcSubjectId].find(f => f.fullName === flag.fullName)) {
                    subjectsFlags[res.zcSubjectId].push(flag);
                }
            }
        }

        for (let res of results) {
            res.subjectSerialNumber = subjects.indexOf(res.ctSubjectId);
            res.trendFlags = subjectsFlags[res.zcSubjectId];
        }
    }
}